import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { BookOpen, Search } from 'lucide-react';
import Seo from '@/components/Seo';

const TERMS = [
  { term: 'BHK', def: 'Bedroom, Hall, Kitchen — the standard way to describe apartment configurations in India. A 2BHK has 2 bedrooms, 1 hall, and 1 kitchen.' },
  { term: 'Carpet Area', def: 'The actual usable area within the walls of an apartment. Excludes the thickness of walls. RERA mandates pricing based on carpet area.' },
  { term: 'Built-up Area', def: 'Carpet area + thickness of inner & outer walls + balcony. Typically 10–15% more than carpet area.' },
  { term: 'Super Built-up Area', def: 'Built-up area + proportionate share of common areas (lifts, lobbies, stairs). Often called "saleable area".' },
  { term: 'Loading', def: 'The percentage difference between super built-up and carpet area. A loading of 30% means 30% extra is added for common areas.' },
  { term: 'RERA', def: 'Real Estate (Regulation and Development) Act, 2016. State-level authority that regulates the real estate sector and protects buyers.' },
  { term: 'Sale Deed', def: 'The primary legal document that transfers property ownership from seller to buyer. Must be registered with the Sub-Registrar.' },
  { term: 'Title Deed', def: 'A document that establishes the legal ownership of a property. Verify chain of title before purchase.' },
  { term: 'Encumbrance Certificate (EC)', def: 'A document showing that a property is free from any monetary or legal liabilities — mortgages, loans, court cases.' },
  { term: 'Stamp Duty', def: 'A state tax paid on property transactions. Ranges from 4–8% of property value, varies by state and gender of buyer.' },
  { term: 'Ready Reckoner Rate', def: 'The minimum value at which a property can be registered, set by the state government. Also called "Circle Rate" in North India.' },
  { term: 'EMI', def: 'Equated Monthly Instalment — the fixed monthly payment a borrower makes to repay a home loan over the loan tenure.' },
  { term: 'LTV (Loan-to-Value)', def: 'The percentage of property value that a bank will finance. Typically 75–80% for home loans, meaning you arrange 20–25% down payment.' },
  { term: 'CIBIL Score', def: 'A 3-digit credit score (300–900) used by Indian banks to assess loan eligibility. 750+ is considered excellent.' },
  { term: 'Pre-EMI', def: 'Interest-only payments made during the construction phase of an under-construction property, before regular EMIs kick in.' },
  { term: 'Floor Space Index (FSI)', def: 'The ratio of total built-up area to plot area. Higher FSI means more vertical construction is allowed.' },
  { term: 'Conveyance Deed', def: 'Transfers all rights of land from the builder/developer to the housing society after a project is completed.' },
  { term: 'Mutation', def: 'The transfer of property records in the local municipal office to the new owner\'s name after purchase. Critical for tax records.' },
  { term: 'TDS on Property', def: 'Tax Deducted at Source — buyer must deduct 1% TDS when purchasing property worth ₹50 lakh or more.' },
  { term: 'Possession Letter', def: 'A document issued by the developer confirming the buyer can take physical possession of the property.' },
  { term: 'Occupancy Certificate (OC)', def: 'A municipality-issued certificate confirming a building is fit for occupation, complies with all approved plans and safety norms.' },
  { term: 'Completion Certificate (CC)', def: 'Issued by the local authority confirming a building is completed as per approved plans.' },
  { term: 'Khata', def: 'A revenue document maintained by municipal authorities (especially in Karnataka) that records property tax liability.' },
  { term: 'Deed of Apartment', def: 'A specific deed used in apartment complexes to transfer ownership of an individual unit and its proportionate share of land.' },
  { term: 'GST on Property', def: 'Goods and Services Tax — applies only to under-construction properties (5% for non-affordable, 1% for affordable). No GST on ready-to-move properties.' },
  { term: 'Section 80C', def: 'Income Tax Act section allowing deduction up to ₹1.5 lakh on home loan principal repayment per year.' },
  { term: 'Section 24(b)', def: 'Allows deduction up to ₹2 lakh per year on home loan interest for a self-occupied property.' },
  { term: 'Capital Gains Tax', def: 'Tax on profit from selling property. Long-term (>2 years) attracts 20% with indexation. Short-term taxed at slab rate.' },
  { term: 'Rental Yield', def: 'Annual rental income divided by property value, expressed as a percentage. 2–4% is typical for Indian metros.' },
  { term: 'Power of Attorney (PoA)', def: 'A legal document authorising someone to act on your behalf in property transactions. Cannot transfer ownership by itself.' },
];

const GlossaryPage = () => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return TERMS;
    const s = search.toLowerCase();
    return TERMS.filter(t => t.term.toLowerCase().includes(s) || t.def.toLowerCase().includes(s));
  }, [search]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(t => {
      const letter = t.term[0].toUpperCase();
      if (!map[letter]) map[letter] = [];
      map[letter].push(t);
    });
    return Object.keys(map).sort().map(letter => ({ letter, items: map[letter].sort((a, b) => a.term.localeCompare(b.term)) }));
  }, [filtered]);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="glossary-page">
      <Seo title="Real Estate Glossary" description="Demystify real estate jargon. 30 India-focused terms — RERA, BHK, Carpet Area, Stamp Duty, EMI, LTV and more." path="/glossary" />
      <div className="container-luxury max-w-4xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-medium mb-3">
            <BookOpen className="h-3.5 w-3.5" /> {TERMS.length} terms · India focused
          </div>
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-3">
            Real Estate Glossary
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Demystify the jargon. Every term you'll encounter when buying, selling, or renting property in India.
          </p>
        </div>

        <div className="bg-white p-6 rounded-sm shadow-sm border border-border/50 mb-8 sticky top-20 z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search terms (e.g., RERA, EMI, Stamp Duty)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 text-base"
              data-testid="glossary-search"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
          </p>
        </div>

        {grouped.length === 0 && (
          <div className="bg-white p-12 rounded-sm border border-border/50 text-center">
            <p className="text-muted-foreground">No terms match "{search}". Try a different keyword.</p>
          </div>
        )}

        <div className="space-y-8">
          {grouped.map(group => (
            <section key={group.letter} data-testid={`glossary-letter-${group.letter}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-[#0F172A] text-[#D4AF37] flex items-center justify-center font-['Plus_Jakarta_Sans'] text-xl font-bold">
                  {group.letter}
                </div>
                <div className="flex-1 h-px bg-border/50" />
              </div>
              <div className="space-y-3">
                {group.items.map(t => (
                  <details key={t.term} className="group bg-white p-5 rounded-sm border border-border/50 hover:border-[#D4AF37]/40 transition-colors">
                    <summary className="font-semibold text-[#0F172A] cursor-pointer flex items-center justify-between">
                      <span>{t.term}</span>
                      <span className="text-[#D4AF37] text-xl group-open:rotate-45 transition-transform">+</span>
                    </summary>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{t.def}</p>
                  </details>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GlossaryPage;
